/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Metrics from './metrics/lazyMetrics';

const buffer = new Set;
const metrics = new Metrics('exc.module-runtime.util');
let metricsTimeout: any;

function trackMetrics(path: string): void {
  buffer.add(path.replace(/\[\d*\]$/, '[]'));
  clearTimeout(metricsTimeout);
  metricsTimeout = setTimeout(() => {
    metrics.event('Object.accessed', Array.from(buffer));
    buffer.clear();
  }, 500);
}

export function gettify(obj: Record<string, any>, path = ''): void {
  if (obj == null || typeof obj !== 'object') {
    return;
  }

  Object.entries(obj).forEach(([key, value]) => {
    let newPath = `${path}${path ? '.' : ''}${key}`;

    Array.isArray(value) ?
      value.forEach((o, i) => gettify(o, `${newPath}[${i}]`)) :
      gettify(value, newPath);

    Object.defineProperty(obj, key, {
      get: () => {
        trackMetrics(newPath);
        return value;
      }
    });
  });
}

export interface IframePerformance {
  done?: number;
  timeOrigin?: number;
  entries: PerformanceEntry[];
  timing: PerformanceTiming;
}

/**
 * Filters performance entries before sending to Shell.
 * @returns performance entries data
 */
export function getPerformanceEntries(): IframePerformance|undefined {
  return {
    done: performance.now(),
    entries: performance.getEntries()
      .filter((e: PerformanceEntry) => (
        e.entryType === 'mark' || e.entryType === 'navigation' || e.entryType === 'paint' ||
        (e.entryType === 'resource' && /runtime.*\.js$/.test(e.name))
      )),
    timeOrigin: performance.timeOrigin,
    timing: performance.timing
  };
}

/**
 * Checks if current window is Unified Shell or iframe.
 * @returns true if running in Shell, false if running in an iframe
 */
export function isShell(): boolean {
  const w = (window as any);
  return (w.parent === w) || ('config' in w && w.config.tis === 1);
}

/**
 * Preloads an asset
 * @param {string} resourceType
 * @param {string} href
 */
function preloadAsset(resourceType: string, href: string): void {
  const {document} = (window as any);
  const preloadLink = document.createElement('link');
  preloadLink.rel = 'preload';
  preloadLink.as = resourceType;
  preloadLink.href = href;
  document.head.appendChild(preloadLink);
}

export function preload(preloads: {[key: string]: any}): void {
  const w = (window as any);
  if (w.config && w.config.preloads) {
    w.config.preloads.forEach((asset: string) => {
      if (asset in preloads) {
        const {resourceType, href} = preloads[asset];
        preloadAsset(resourceType, href);
      }
    });
  }
}
